/* ==========================================================================
   Double Jump Video Games — custom theme behavior
   ========================================================================== */

(function () {
  'use strict';

  /* ------------------------------------------------------------------------
     Rotating search placeholder

     On a 30,000-product catalog the search field has to do more than sit
     there: it has to suggest what can be searched. This types real examples
     drawn from across the catalog — a game title, a card, a platform, a
     category — so people learn the breadth without being told.

     It stops the instant the field is focused so it never competes with
     typing, and it does not run at all when the visitor prefers reduced
     motion; they get the plain placeholder instead.
     ---------------------------------------------------------------------- */

  var TYPE_MS = 55; // per character while typing
  var ERASE_MS = 28; // per character while erasing
  var HOLD_MS = 1900; // pause on a completed phrase
  var GAP_MS = 350; // pause after erasing, before the next phrase

  function initRotatingPlaceholder(input) {
    var base = input.getAttribute('data-placeholder-base') || '';
    var items;

    try {
      items = JSON.parse(input.getAttribute('data-placeholder-items') || '[]');
    } catch (e) {
      items = [];
    }

    if (!items.length) return;

    var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    var timer = null;
    var index = 0;
    var charCount = 0;
    var erasing = false;
    var stopped = false;

    function clear() {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    }

    function rest() {
      input.placeholder = base;
    }

    function stop() {
      stopped = true;
      clear();
      rest();
    }

    function start() {
      if (reduceMotion.matches) {
        rest();
        return;
      }
      stopped = false;
      index = 0;
      charCount = 0;
      erasing = false;
      clear();
      timer = setTimeout(step, GAP_MS);
    }

    function step() {
      if (stopped) return;

      var phrase = items[index % items.length];

      if (!erasing) {
        charCount += 1;
        input.placeholder = base + ' — ' + phrase.slice(0, charCount);

        if (charCount >= phrase.length) {
          erasing = true;
          timer = setTimeout(step, HOLD_MS);
          return;
        }
        timer = setTimeout(step, TYPE_MS);
        return;
      }

      charCount -= 1;
      input.placeholder = base + (charCount > 0 ? ' — ' + phrase.slice(0, charCount) : '');

      if (charCount <= 0) {
        erasing = false;
        index += 1;
        timer = setTimeout(step, GAP_MS);
        return;
      }
      timer = setTimeout(step, ERASE_MS);
    }

    // Never compete with the person actually using the field.
    input.addEventListener('focus', stop);
    input.addEventListener('input', stop);
    input.addEventListener('blur', function () {
      if (input.value === '') start();
    });

    // Honor a mid-session change to the motion preference.
    var onPreferenceChange = function () {
      if (reduceMotion.matches) {
        stop();
      } else if (document.activeElement !== input && input.value === '') {
        start();
      }
    };
    if (typeof reduceMotion.addEventListener === 'function') {
      reduceMotion.addEventListener('change', onPreferenceChange);
    }

    // Don't animate offscreen or in a background tab.
    document.addEventListener('visibilitychange', function () {
      if (document.hidden) {
        clear();
      } else if (!stopped && document.activeElement !== input) {
        timer = setTimeout(step, GAP_MS);
      }
    });

    start();
  }

  /* ------------------------------------------------------------------------
     Hero carousel

     Deliberately manual. A hero that advances on its own competes with the
     search field for attention, and search is how most people navigate a
     30,000-product catalog. Arrows only, and only when there is more than
     one slide.
     ---------------------------------------------------------------------- */

  function initHero(root) {
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-djvg-slide]'));
    if (slides.length < 2) return;

    var prev = root.querySelector('[data-djvg-hero-prev]');
    var next = root.querySelector('[data-djvg-hero-next]');
    var status = root.querySelector('[data-djvg-hero-status]');
    var index = 0;

    function show(nextIndex) {
      index = (nextIndex + slides.length) % slides.length;

      slides.forEach(function (slide, i) {
        var active = i === index;
        slide.classList.toggle('is-active', active);
        if (active) {
          slide.removeAttribute('aria-hidden');
        } else {
          slide.setAttribute('aria-hidden', 'true');
        }
      });

      if (status) {
        status.textContent = 'Slide ' + (index + 1) + ' of ' + slides.length;
      }
    }

    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    // Arrow keys once a control has focus.
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') show(index - 1);
      if (event.key === 'ArrowRight') show(index + 1);
    });
  }

  /* ------------------------------------------------------------------------
     Condition help bubble

     Positioned against the trigger and nudged to stay on screen, so it never
     runs off the right edge on a narrow viewport. Opens on hover, focus and
     tap; closes on leave, blur, Escape, or a click elsewhere.
     ---------------------------------------------------------------------- */

  function initHelp(root) {
    var trigger = root.querySelector('.djvg-help__trigger');
    var bubble = root.querySelector('.djvg-help__bubble');
    if (!trigger || !bubble) return;

    var open = false;

    function place() {
      // Reset before measuring, or a previous nudge compounds.
      bubble.style.left = '';
      bubble.style.transform = '';

      var box = bubble.getBoundingClientRect();
      var margin = 12;
      var overflowRight = box.right - (window.innerWidth - margin);
      var overflowLeft = margin - box.left;

      if (overflowRight > 0) {
        bubble.style.transform = 'translateX(' + -Math.ceil(overflowRight) + 'px)';
      } else if (overflowLeft > 0) {
        bubble.style.transform = 'translateX(' + Math.ceil(overflowLeft) + 'px)';
      }
    }

    function show() {
      if (open) return;
      open = true;
      bubble.hidden = false;
      place();
    }

    function hide() {
      if (!open) return;
      open = false;
      bubble.hidden = true;
    }

    root.addEventListener('mouseenter', show);
    root.addEventListener('mouseleave', hide);
    trigger.addEventListener('focus', show);
    trigger.addEventListener('blur', hide);
    trigger.addEventListener('click', function (event) {
      event.preventDefault();
      if (open) { hide(); } else { show(); }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') hide();
    });

    document.addEventListener('click', function (event) {
      if (!root.contains(event.target)) hide();
    });

    window.addEventListener('resize', function () {
      if (open) place();
    });
  }

  /* A Shorts tile is a poster until it is asked for. Five homepage embeds
     would otherwise pull several hundred kilobytes and a set of third-party
     cookies before anyone has decided to watch anything, so the iframe is
     built on click and nothing reaches YouTube before then. */
  function initShort(button) {
    button.addEventListener('click', function () {
      var id = button.getAttribute('data-djvg-short');
      if (!id) return;

      var frame = document.createElement('iframe');
      frame.className = 'djvg-shorts__frame';
      frame.src =
        'https://www.youtube-nocookie.com/embed/' +
        encodeURIComponent(id) +
        '?autoplay=1&rel=0&playsinline=1';
      frame.title = button.textContent.trim() || 'YouTube video';
      frame.allow =
        'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
      frame.allowFullscreen = true;
      frame.loading = 'lazy';

      button.replaceWith(frame);
    });
  }

  /* A bundled cut-out can be selected before its file has been added to the
     theme. A broken image in the middle of a gradient panel is worse than no
     image, so the artwork removes itself. */
  function initOptionalArt(img) {
    img.addEventListener('error', function () {
      var holder = img.closest('.djvg-band__art, .djvg-tile__art');
      if (holder) holder.hidden = true;
    });
    if (img.complete && img.naturalWidth === 0) {
      var holder = img.closest('.djvg-band__art, .djvg-tile__art');
      if (holder) holder.hidden = true;
    }
  }

  /* The collection hero's back arrow. Collections have no parent to link up
     to, so it goes back a page when the shopper came from this site, and
     otherwise follows its link (all collections, or what the section sets). */
  function initBackLink(link) {
    link.addEventListener('click', function (event) {
      var ref;
      try {
        ref = document.referrer ? new URL(document.referrer) : null;
      } catch (e) {
        ref = null;
      }
      if (ref && ref.origin === window.location.origin && window.history.length > 1) {
        event.preventDefault();
        window.history.back();
      }
    });
  }

  /* Carousel: a scroll-snapping track with two buttons. No library, no
     autoplay, and it degrades to a plain horizontal scroll without JS —
     which is what a touch device does with it anyway. */
  function initCarousel(root) {
    var track = root.querySelector('[data-djvg-track]');
    var prev = root.querySelector('[data-djvg-prev]');
    var next = root.querySelector('[data-djvg-next]');
    if (!track) return;

    /* Animated by hand rather than with smooth scrolling, native or CSS.
       Where the platform has smooth scrolling switched off, neither
       scrollBy({behavior:'smooth'}) nor scroll-behavior falls back to an
       instant jump — both silently do nothing, which leaves the arrows dead.
       A requestAnimationFrame tween always moves. */
    var animating = null;

    function page(direction) {
      var limit = track.scrollWidth - track.clientWidth;
      var delta = direction * Math.max(240, track.clientWidth * 0.82);
      var target = Math.max(0, Math.min(limit, track.scrollLeft + delta));

      if (animating) cancelAnimationFrame(animating);

      /* No animation when it cannot be seen: a hidden tab pauses
         requestAnimationFrame outright, so tweening there would leave the
         track exactly where it was. */
      if (
        document.hidden ||
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ) {
        track.scrollLeft = target;
        sync();
        return;
      }

      var from = track.scrollLeft;
      var span = target - from;
      var started = null;

      function step(now) {
        if (started === null) started = now;
        var progress = Math.min(1, (now - started) / 380);
        var eased =
          progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2;
        track.scrollLeft = from + span * eased;
        if (progress < 1) {
          animating = requestAnimationFrame(step);
        } else {
          animating = null;
          sync();
        }
      }

      animating = requestAnimationFrame(step);
    }

    function sync() {
      var max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      root.classList.toggle('djvg-carousel--static', max <= 0);
    }

    if (prev) prev.addEventListener('click', function () { page(-1); });
    if (next) next.addEventListener('click', function () { page(1); });
    track.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);

    /* Cards are lazy-loaded, so the track is narrower at init than it will be
       a moment later. Without this the arrows can start out disabled and stay
       that way. */
    if (window.ResizeObserver) {
      new ResizeObserver(sync).observe(track);
    }

    sync();
  }

  /* Pull the channel's Shorts and replace whatever the theme rendered.

     A channel's uploads playlist is its id with UC swapped for UU; swapping
     UC for UUSH gives a Shorts-only playlist, so one playlistItems call costs
     a single quota unit and no duration filtering is needed to separate a
     Short from a normal upload.

     The channel RSS feed would need no key at all but sends no CORS header,
     so a browser cannot read it. The Data API reflects the origin, which is
     why this uses the API and why the key has to be referrer-restricted.

     Results are cached for six hours so a busy day costs a handful of units,
     and any failure leaves the server-rendered blocks exactly as they are. */
  var SHORTS_TTL = 6 * 60 * 60 * 1000;

  function shortsTile(id, title) {
    var li = document.createElement('li');
    li.className = 'djvg-shorts__item';

    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'djvg-shorts__tile';
    button.setAttribute('data-djvg-short', id);

    var poster = document.createElement('span');
    poster.className = 'djvg-shorts__poster';
    var img = document.createElement('img');
    img.src = 'https://i.ytimg.com/vi/' + encodeURIComponent(id) + '/hqdefault.jpg';
    img.alt = '';
    img.width = 480;
    img.height = 360;
    img.loading = 'lazy';
    poster.appendChild(img);

    var play = document.createElement('span');
    play.className = 'djvg-shorts__play';
    play.setAttribute('aria-hidden', 'true');
    play.innerHTML = '<svg viewBox="0 0 24 24" fill="currentColor"><path d="M8 5.5v13l11-6.5z"/></svg>';

    var label = document.createElement('span');
    label.className = 'djvg-shorts__label';
    label.textContent = title || 'Play';

    button.appendChild(poster);
    button.appendChild(play);
    button.appendChild(label);
    li.appendChild(button);
    return li;
  }

  function renderShorts(root, items) {
    if (!items || !items.length) return;
    var track = root.querySelector('[data-djvg-track]');
    var empty = root.querySelector('.djvg-band__empty');
    if (empty) empty.remove();

    if (!track) {
      track = document.createElement('ul');
      track.className = 'djvg-shorts__row';
      track.setAttribute('role', 'list');
      track.setAttribute('data-djvg-track', '');
      var next = root.querySelector('[data-djvg-next]');
      root.insertBefore(track, next);
    }

    track.textContent = '';
    items.forEach(function (item) {
      track.appendChild(shortsTile(item.id, item.title));
    });
    track.querySelectorAll('[data-djvg-short]').forEach(initShort);
    initCarousel(root);
  }

  function initShortsFeed(root) {
    var channel = root.getAttribute('data-channel');
    var key = root.getAttribute('data-key');
    var count = parseInt(root.getAttribute('data-count'), 10) || 10;
    if (!channel || !key || channel.indexOf('UC') !== 0) return;

    var playlist = 'UUSH' + channel.slice(2);
    var cacheKey = 'djvg:shorts:' + playlist + ':' + count;

    try {
      var cached = JSON.parse(localStorage.getItem(cacheKey) || 'null');
      if (cached && Date.now() - cached.at < SHORTS_TTL) {
        renderShorts(root, cached.items);
        return;
      }
    } catch (e) {
      /* private mode, cleared storage, blocked site data — just refetch */
    }

    var url =
      'https://www.googleapis.com/youtube/v3/playlistItems?part=snippet&maxResults=' +
      encodeURIComponent(count) +
      '&playlistId=' +
      encodeURIComponent(playlist) +
      '&key=' +
      encodeURIComponent(key);

    fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('YouTube responded ' + response.status);
        return response.json();
      })
      .then(function (data) {
        var items = (data.items || [])
          .map(function (entry) {
            var snippet = entry.snippet || {};
            var resource = snippet.resourceId || {};
            return { id: resource.videoId, title: snippet.title };
          })
          .filter(function (item) {
            return item.id;
          });

        if (!items.length) return;
        try {
          localStorage.setItem(cacheKey, JSON.stringify({ at: Date.now(), items: items }));
        } catch (e) {
          /* storage unavailable; the row still renders, it just refetches */
        }
        renderShorts(root, items);
      })
      .catch(function () {
        /* Leave the blocks the theme rendered. A missing key, a referrer
           mismatch or an exhausted quota should not empty the section. */
      });
  }

  /* Set index: a live filter over a list that runs to hundreds of entries.
     Edition chips are derived from what rendered rather than hard-coded, so an
     edition appears only once the shop stocks a set in it. Edition here means
     the printing of the physical product — a Japanese-print set, a PAL disc —
     not the visitor's locale. */
  function initSetIndex(root) {
    var list = root.querySelector('[data-djvg-setlist]');
    var input = root.querySelector('[data-djvg-setfilter]');
    var tally = root.querySelector('[data-djvg-settally]');
    var empty = root.querySelector('[data-djvg-setempty]');
    var chipbox = root.querySelector('[data-djvg-setlangs]');
    if (!list) return;

    var items = [].slice.call(list.querySelectorAll('.djvg-setx__item'));
    var langs = [];
    items.forEach(function (li) {
      var l = li.getAttribute('data-edition');
      if (l && langs.indexOf(l) === -1) langs.push(l);
    });

    var active = null;

    function apply() {
      var q = (input && input.value || '').trim().toLowerCase();
      var shown = 0;
      items.forEach(function (li) {
        var okLang = !active || li.getAttribute('data-edition') === active;
        var okText = !q || li.getAttribute('data-name').indexOf(q) !== -1;
        var on = okLang && okText;
        li.hidden = !on;
        if (on) shown++;
      });
      if (tally) tally.textContent = shown + ' of ' + items.length;
      if (empty) empty.hidden = shown !== 0;
    }

    if (chipbox && langs.length > 1) {
      langs.forEach(function (l) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'djvg-setx__chip';
        b.textContent = l;
        b.setAttribute('aria-pressed', 'false');
        b.addEventListener('click', function () {
          active = active === l ? null : l;
          [].forEach.call(chipbox.children, function (c) {
            c.setAttribute('aria-pressed', c === b && active ? 'true' : 'false');
          });
          apply();
        });
        chipbox.appendChild(b);
      });
    }

    if (input) input.addEventListener('input', apply);
    apply();
  }

  /* Pre-order countdown. The open/closed decision is made server side; this
     only ticks the clock, and swaps to the closed panel if the deadline
     passes while someone is sitting on the page. */
  // Click-to-play. The player is only fetched once someone asks for it, so a
  // visitor who never presses play never loads YouTube at all.
  function initGallery(root) {
    var buttons = root.querySelectorAll('[data-djvg-video]');
    if (!buttons.length) return;

    Array.prototype.forEach.call(buttons, function (btn) {
      btn.addEventListener('click', function () {
        var src = btn.getAttribute('data-djvg-video');
        if (!src) return;

        var frame = document.createElement('iframe');
        frame.className = 'djvg-gal__frame';
        frame.setAttribute('src', src);
        frame.setAttribute('title', btn.getAttribute('aria-label') || 'Video');
        frame.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
        frame.setAttribute('allowfullscreen', '');

        btn.replaceWith(frame);
        // The click that swapped it in was the request to watch, so focus moves
        // to the player rather than being left on a button that no longer exists.
        frame.setAttribute('tabindex', '-1');
        frame.focus({ preventScroll: true });
      });
    });
  }

  // Reserve without leaving the page: add over fetch, then hand the drawer its
  // freshly rendered markup and open it. The form still works as a plain post
  // if this never runs — the submit is only intercepted once we are certain we
  // can take over, so a failure here falls back rather than dead-ends.
  // Opens the side cart. Bound here rather than relying on Dawn's
  // #cart-icon-bubble hook, because that hook's element gets its innerHTML
  // replaced on every add.
  function initCartOpener(link) {
    link.addEventListener('click', function (event) {
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.open !== 'function') return;   // follow the href
      event.preventDefault();
      drawer.open(link);
    });
  }

  function initDrawerAdd(form) {
    var button = form.querySelector('[type="submit"]');

    form.addEventListener('submit', function (event) {
      // Looked up on submit, not at startup. cart-drawer.js loads after this
      // file — it has to, because it extends a class the drawer's own snippet
      // defines — so the element is not upgraded yet when this runs. Checking
      // here also means a page without a drawer just posts the form.
      var drawer = document.querySelector('cart-drawer');
      if (!drawer || typeof drawer.renderContents !== 'function') return;

      event.preventDefault();
      if (button && button.getAttribute('aria-disabled') === 'true') return;

      var data = new FormData(form);
      // Ask for the drawer's markup in the same round trip, so the panel that
      // opens is already showing what was just added.
      data.append('sections', 'cart-drawer');
      data.append('sections_url', window.location.pathname);

      if (button) {
        button.setAttribute('aria-disabled', 'true');
        button.classList.add('loading');
      }

      fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: data
      })
        .then(function (res) { return res.json(); })
        .then(function (payload) {
          if (payload.status) throw new Error(payload.description || payload.message);

          // renderContents clears is-empty from .drawer__inner, but open()
          // tests it on the drawer element itself and then reaches for
          // .drawer__inner-empty, which is gone once there is something in the
          // cart. Left set, focus trapping throws on null and the drawer opens
          // untrapped. Something was just added, so it is not empty.
          drawer.classList.remove('is-empty');
          drawer.renderContents(payload);
          refreshCartCount();
        })
        .catch(function () {
          // Whatever went wrong, the plain post still works, so send them
          // through it rather than leaving a button that does nothing.
          form.removeAttribute('data-djvg-add-to-drawer');
          form.submit();
        })
        .finally(function () {
          if (button) {
            button.removeAttribute('aria-disabled');
            button.classList.remove('loading');
          }
        });
    });
  }

  // The count in the bar is server-rendered on load; after an add it is read
  // back off the drawer, which the response has just re-rendered.
  function refreshCartCount() {
    var badge = document.querySelector('[data-djvg-cart-count]');
    if (!badge) return;
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        badge.textContent = cart.item_count;
        badge.hidden = cart.item_count === 0;
      })
      .catch(function () {});
  }

  function initPreorder(root) {
      var closesIso = root.getAttribute('data-closes-at');
      var releaseIso = root.getAttribute('data-release-at');

      var clock = root.querySelector('[data-djvg-clock]');
      var expired = root.querySelector('[data-djvg-expired]');
      var released = root.querySelector('[data-djvg-released]');
      var live = root.querySelector('[data-djvg-live]');
      var out = {
        d: root.querySelector('[data-d]'),
        h: root.querySelector('[data-h]'),
        m: root.querySelector('[data-m]'),
        s: root.querySelector('[data-s]')
      };

      function pad(n) {
        return n < 10 ? '0' + n : String(n);
      }

      // The release date drives the digits when there is one. A sale deadline
      // only takes the clock if no release date was given, so the two never
      // fight over the same four numbers.
      var countIso = releaseIso || closesIso;
      var countEnds = countIso ? Date.parse(countIso) : NaN;
      var countTimer = null;

      function paint() {
        var left = countEnds - Date.now();
        if (left <= 0) {
          if (countTimer) clearInterval(countTimer);
          // Release day arriving is not the sale ending: swap the clock for the
          // out-now line and leave the buy area exactly as it is.
          if (clock) clock.hidden = true;
          if (releaseIso && released) released.hidden = false;
          return;
        }
        if (clock) clock.hidden = false;
        var s = Math.floor(left / 1000);
        if (out.d) out.d.textContent = Math.floor(s / 86400);
        if (out.h) out.h.textContent = pad(Math.floor(s / 3600) % 24);
        if (out.m) out.m.textContent = pad(Math.floor(s / 60) % 60);
        if (out.s) out.s.textContent = pad(s % 60);
      }

      if (countIso && !isNaN(countEnds)) {
        paint();
        countTimer = setInterval(paint, 1000);
      }

      // Closing the sale is a separate job with its own clock, so a page can
      // count down to release while still shutting at its own deadline.
      var closeEnds = closesIso ? Date.parse(closesIso) : NaN;
      if (closesIso && !isNaN(closeEnds)) {
        var closeTimer = setInterval(function () {
          if (closeEnds - Date.now() > 0) return;
          clearInterval(closeTimer);
          if (countTimer) clearInterval(countTimer);
          if (clock) clock.hidden = true;
          if (live) live.hidden = true;
          if (expired) expired.hidden = false;
        }, 1000);
      }
    }

  function init() {
    document.querySelectorAll('[data-djvg-rotate]').forEach(initRotatingPlaceholder);
    document.querySelectorAll('[data-djvg-hero]').forEach(initHero);
    document.querySelectorAll('[data-djvg-back]').forEach(initBackLink);
    document.querySelectorAll('[data-djvg-help]').forEach(initHelp);
    document.querySelectorAll('[data-djvg-short]').forEach(initShort);
    document.querySelectorAll('[data-djvg-optional-art]').forEach(initOptionalArt);
    document.querySelectorAll('[data-djvg-carousel]').forEach(initCarousel);
    document.querySelectorAll('[data-djvg-shorts-feed]').forEach(initShortsFeed);
    document.querySelectorAll('[data-djvg-setindex]').forEach(initSetIndex);
    document.querySelectorAll('[data-djvg-preorder]').forEach(initPreorder);
    // The About page's store-tour video uses the same click-to-play facade.
    document.querySelectorAll('.djvg-gal, .djvg-fv').forEach(initGallery);
    document.querySelectorAll('[data-djvg-add-to-drawer]').forEach(initDrawerAdd);
    document.querySelectorAll('[data-djvg-cart-open]').forEach(initCartOpener);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
